<template>
  <svg
    class="compass-dial"
    :class="{ 'compass-dial--empty': value == null }"
    :width="size"
    :height="size"
    :viewBox="`0 0 ${size} ${size}`"
    role="slider"
    tabindex="0"
    :aria-valuemin="0"
    :aria-valuemax="360"
    :aria-valuenow="value"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="stopDrag"
    @keydown.up.prevent="step(1)"
    @keydown.right.prevent="step(1)"
    @keydown.down.prevent="step(-1)"
    @keydown.left.prevent="step(-1)"
  >
    <circle class="compass-dial__ring" :cx="center" :cy="center" :r="radius" />

    <line
      v-for="tick in ticks"
      :key="tick.degrees"
      class="compass-dial__tick"
      :class="{ 'compass-dial__tick--main': tick.main }"
      :x1="tick.x1"
      :y1="tick.y1"
      :x2="tick.x2"
      :y2="tick.y2"
    />

    <text
      v-for="letter in letters"
      :key="letter.key"
      class="compass-dial__letter"
      :x="letter.x"
      :y="letter.y"
      text-anchor="middle"
      dominant-baseline="central"
    >
      {{ letter.label }}
    </text>

    <g
      class="compass-dial__needle"
      :class="{ 'compass-dial__needle--dragging': dragging }"
      :style="{ transform: `rotate(${rotation}deg)` }"
    >
      <line :x1="center" :y1="center + 8" :x2="center" :y2="center - radius + 16" />
      <polygon
        :points="`${center},${center - radius + 8} ${center - 4},${center - radius + 17} ${center + 4},${
          center - radius + 17
        }`"
      />
    </g>

    <circle class="compass-dial__hub" :cx="center" :cy="center" r="2.5" />
  </svg>
</template>

<script>
export default {
  props: {
    value: Number,
    size: {
      type: Number,
      default: 72,
    },
  },
  data() {
    return {
      rotation: this.value == null ? 0 : this.value,
      dragging: false,
    }
  },
  watch: {
    value(next) {
      if (next == null) return

      const current = ((this.rotation % 360) + 360) % 360
      let delta = (next % 360) - current
      if (delta > 180) delta -= 360
      if (delta < -180) delta += 360

      this.rotation += delta
    },
  },
  computed: {
    center() {
      return this.size / 2
    },
    radius() {
      return this.center - 2
    },
    ticks() {
      return Array.from({ length: 16 }, (item, index) => {
        const degrees = index * 22.5
        const main = index % 4 === 0
        const outer = this.point(degrees, this.radius - 2)
        const inner = this.point(degrees, this.radius - 2 - (main ? 6 : 3.5))

        return { degrees, main, x1: outer.x, y1: outer.y, x2: inner.x, y2: inner.y }
      })
    },
    letters() {
      return [
        { key: 'N', degrees: 0 },
        { key: 'E', degrees: 90 },
        { key: 'S', degrees: 180 },
        { key: 'W', degrees: 270 },
      ].map(letter => ({
        ...letter,
        label: this.$t(`cardinalDirection${letter.key}`),
        ...this.point(letter.degrees, this.radius - 16),
      }))
    },
  },
  methods: {
    point(degrees, r) {
      const radians = (degrees * Math.PI) / 180
      return {
        x: this.center + r * Math.sin(radians),
        y: this.center - r * Math.cos(radians),
      }
    },
    onPointerDown(event) {
      event.preventDefault()
      this.$el.focus()
      this.dragging = true
      this.$el.setPointerCapture(event.pointerId)
      this.setFromEvent(event)
    },
    onPointerMove(event) {
      if (!this.dragging) return
      this.setFromEvent(event)
    },
    setFromEvent(event) {
      const bounds = this.$el.getBoundingClientRect()
      const x = event.clientX - bounds.left - bounds.width / 2
      const y = event.clientY - bounds.top - bounds.height / 2
      const degrees = (Math.round((Math.atan2(x, -y) * 180) / Math.PI) + 360) % 360

      this.$emit('input', degrees)
    },
    stopDrag() {
      this.dragging = false
    },
    step(delta) {
      const current = this.value == null ? 0 : Math.round(this.value)
      this.$emit('input', (((current + delta) % 360) + 360) % 360)
    },
  },
}
</script>

<style lang="scss">
.compass-dial {
  color: var(--dark-grey);
  cursor: pointer;
  touch-action: none;
  outline-offset: 2px;

  &__ring {
    fill: white;
    stroke: var(--grey);
  }

  &__tick {
    stroke: currentColor;
    stroke-width: 1;
    opacity: 0.6;

    &--main {
      stroke-width: 1.5;
      opacity: 1;
    }
  }

  &__letter {
    fill: currentColor;
    font-size: 9px;
    user-select: none;
  }

  &__needle {
    transform-origin: center;
    transform-box: view-box;
    transition: transform 200ms ease;

    &--dragging {
      transition: none;
    }

    line {
      stroke: var(--primary);
      stroke-width: 2;
      stroke-linecap: round;
    }

    polygon {
      fill: var(--primary);
    }
  }

  &__hub {
    fill: var(--primary);
  }

  &--empty {
    opacity: 0.6;

    .compass-dial__hub {
      fill: currentColor;
    }

    .compass-dial__needle {
      line {
        stroke: currentColor;
      }

      polygon {
        fill: currentColor;
      }
    }
  }
}
</style>
